import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Users, 
  ShoppingBag, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  Euro,
  Calendar,
  Activity,
  RefreshCw,
  FileText,
  TrendingDown
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useToast } from '../../hooks/use-toast';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import LoadingSpinner from '../LoadingSpinner';

const AdminDashboard = () => {
  const { toast } = useToast();
  const { adminToken } = useAdminAuth();
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalUsers: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0,
    recentOrders: []
  });
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
    
    // Auto-refresh ogni 2 minuti
    const interval = setInterval(() => {
      loadDashboardStats();
    }, 120000); // 2 minuti
    
    return () => clearInterval(interval);
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Carica statistiche
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/stats`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Errore nel caricamento delle statistiche');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setStats({
          totalOrders: data.stats.orders.total,
          totalUsers: data.stats.users.total,
          totalRevenue: data.stats.revenue.total,
          pendingOrders: data.stats.orders.pending,
          completedOrders: data.stats.orders.completed,
          ordersToday: data.stats.orders.today,
          ordersThisMonth: data.stats.orders.this_month,
          revenueThisMonth: data.stats.revenue.this_month,
          totalQuotes: data.stats.quotes.total,
          pendingQuotes: data.stats.quotes.pending,
          acceptedQuotes: data.stats.quotes.accepted,
          quotesToday: data.stats.quotes.today,
          newUsersMonth: data.stats.users.new_this_month,
          recentOrders: data.stats.recent_orders,
          recentQuotes: data.stats.recent_quotes,
          topProducts: data.stats.top_products,
          statusDistribution: data.stats.status_distribution,
          salesChart: data.stats.sales_chart
        });
      } else {
        throw new Error(data.error || 'Errore sconosciuto');
      }
      
      // Carica follow-up
      const followupResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/followups`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      if (followupResponse.ok) {
        const followupData = await followupResponse.json();
        if (followupData.success) {
          setFollowups(followupData.followups || []);
        }
      }
      
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      toast({
        title: "Errore nel caricamento",
        description: error.message || "Impossibile caricare le statistiche della dashboard",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending_payment':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'payment_confirmed':
      case 'in_production':
        return <Package className="w-4 h-4 text-blue-600" />;
      case 'shipped':
        return <TrendingUp className="w-4 h-4 text-purple-600" />;
      case 'delivered':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending_payment':
        return 'In attesa pagamento';
      case 'payment_confirmed':
        return 'Pagamento confermato';
      case 'in_production':
        return 'In lavorazione';
      case 'shipped':
        return 'Spedito';
      case 'delivered':
        return 'Consegnato';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_payment':
        return 'bg-yellow-100 text-yellow-800';
      case 'payment_confirmed':
      case 'in_production':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner message="Caricamento dashboard..." />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="border-b border-gray-200 pb-4 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard Admin</h1>
            <p className="mt-2 text-gray-600">
              Panoramica generale del sistema • Aggiornamento automatico ogni 2 minuti
            </p>
          </div>
          <button
            onClick={() => loadDashboardStats()}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Orders */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Ordini Totali</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalOrders}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <ShoppingBag className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Total Users */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Utenti Registrati</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalUsers}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* Total Revenue */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Fatturato Totale</p>
                <p className="text-3xl font-bold text-gray-900">€{stats.totalRevenue.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Euro className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Pending Orders */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Ordini in Sospeso</p>
                <p className="text-3xl font-bold text-gray-900">{stats.pendingOrders}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          {/* Total Quotes */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-amber-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Preventivi Totali</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalQuotes || 0}</p>
                <p className="text-xs text-gray-500 mt-1">In attesa: {stats.pendingQuotes || 0}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <FileText className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>

          {/* Revenue This Month */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-teal-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Fatturato Mensile</p>
                <p className="text-3xl font-bold text-gray-900">€{(stats.revenueThisMonth || 0).toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">{stats.ordersThisMonth || 0} ordini</p>
              </div>
              <div className="p-3 bg-teal-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </div>

          {/* Orders Today */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Ordini Oggi</p>
                <p className="text-3xl font-bold text-gray-900">{stats.ordersToday || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Preventivi: {stats.quotesToday || 0}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Calendar className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          {/* New Users This Month */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Nuovi Utenti</p>
                <p className="text-3xl font-bold text-gray-900">{stats.newUsersMonth || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Questo mese</p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-full">
                <Activity className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Follow-up Programmati */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-purple-600" />
              Follow-up Programmati
            </h2>
            <span className="text-sm text-gray-500">
              {followups.length} attivi
            </span>
          </div>
          
          {followups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Nessun follow-up programmato</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {followups.map((followup, index) => {
                const isOverdue = followup.is_overdue;
                const isToday = new Date(followup.followup_date).toDateString() === new Date().toDateString();
                
                return (
                  <Link
                    key={index}
                    to={`/admin/customers/${followup.customer_id}`}
                    className={`block p-4 rounded-lg border-l-4 hover:shadow-md transition-shadow ${
                      isOverdue 
                        ? 'border-red-500 bg-red-50' 
                        : isToday 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-blue-500 bg-blue-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">
                            {followup.customer_name}
                          </h3>
                          {isOverdue && (
                            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-800 rounded-full">
                              Scaduto
                            </span>
                          )}
                          {isToday && !isOverdue && (
                            <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full">
                              Oggi
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {followup.note_text}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>
                              {new Date(followup.followup_date).toLocaleDateString('it-IT', {
                                day: '2-digit',
                                month: 'short'
                              })} alle {followup.followup_time}
                            </span>
                          </div>
                          {followup.followup_channel && (
                            <div className="flex items-center gap-1">
                              {followup.followup_channel === 'phone' && '📞'}
                              {followup.followup_channel === 'email' && '📧'}
                              {followup.followup_channel === 'whatsapp' && '💬'}
                              {followup.followup_channel === 'meeting' && '🤝'}
                              {followup.followup_channel === 'other' && '📝'}
                              <span className="capitalize">{followup.followup_channel}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <AlertTriangle className={`w-5 h-5 ml-3 ${
                        isOverdue ? 'text-red-500' : isToday ? 'text-orange-500' : 'text-blue-500'
                      }`} />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Orders List */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Ordini Recenti</h2>
                <Link
                  to="/admin/orders"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Vedi tutti →
                </Link>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {stats.recentOrders.map((order) => (
                <div key={order.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <p className="text-sm font-medium text-gray-900">#{order.id}</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{order.customerName}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(order.createdAt).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">€{order.amount.toFixed(2)}</p>
                      <div className="flex items-center justify-end mt-1">
                        {getStatusIcon(order.status)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Azioni Rapide</h2>
            </div>
            <div className="p-6 space-y-4">
              <Link
                to="/admin/orders"
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="p-2 bg-blue-100 rounded-lg mr-4">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Gestisci Ordini</p>
                  <p className="text-sm text-gray-600">Visualizza e aggiorna lo stato degli ordini</p>
                </div>
              </Link>

              <Link
                to="/admin/users"
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="p-2 bg-green-100 rounded-lg mr-4">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Gestisci Utenti</p>
                  <p className="text-sm text-gray-600">Visualizza e gestisci gli account utente</p>
                </div>
              </Link>

              <Link
                to="/admin/products"
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="p-2 bg-purple-100 rounded-lg mr-4">
                  <Activity className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Gestisci Prodotti</p>
                  <p className="text-sm text-gray-600">Modifica liquori, ingredienti e collezioni</p>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Stato Sistema</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-sm text-green-800">Database Online</span>
            </div>
            <div className="flex items-center p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-sm text-green-800">API Funzionanti</span>
            </div>
            <div className="flex items-center p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-sm text-green-800">Email Service Attivo</span>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;